export const LOGO_MAX_SIZE = 512

const SVG_MIME = 'image/svg+xml'

// Downscales an image file to fit within `max`x`max` and returns a PNG data URL.
// Done in the browser so the API needs no image library: it only ever stores the bytes.
// PNG rather than JPEG because logos are usually transparent, and the header they sit on
// is #d1d6d8 rather than white.
//
// SVG goes through a separate path: `createImageBitmap` refuses SVG blobs in Chrome and
// Safari, and a vector has no native resolution to downscale from.
export async function resizeImageToDataURL(file, max = LOGO_MAX_SIZE) {
  if (!file || !(file.type?.startsWith('image/') || isSvgFile(file))) {
    throw new Error('Le fichier sélectionné n’est pas une image.')
  }

  const { source, width, height, release } = isSvgFile(file)
    ? await loadVector(file, max)
    : await loadBitmap(file, max)

  try {
    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const ctx = canvas.getContext('2d')
    ctx.imageSmoothingQuality = 'high'
    ctx.drawImage(source, 0, 0, width, height)

    return canvas.toDataURL('image/png')
  } finally {
    release()
  }
}

// Some systems hand over a .svg with an empty MIME type, so the extension is a fallback.
function isSvgFile(file) {
  return file?.type === SVG_MIME || (!file?.type && /\.svg$/i.test(file?.name || ''))
}

async function loadBitmap(file, max) {
  const bitmap = await createImageBitmap(file)
  // Never upscale: a small logo stays at its native size.
  const scale = Math.min(1, max / bitmap.width, max / bitmap.height)
  return {
    source: bitmap,
    width: Math.max(1, Math.round(bitmap.width * scale)),
    height: Math.max(1, Math.round(bitmap.height * scale)),
    release: () => bitmap.close?.(),
  }
}

// Rasterises the vector straight at the target size — the deliberate upscale the bitmap
// branch refuses, since there is no resolution to lose. Rendering happens inside an <img>,
// which is the browser's secure static mode: scripts never run and external references
// (webfonts, linked images) are not fetched, so a logo relying on those loses them.
async function loadVector(file, max) {
  const svg = parseSvg(await file.text())
  const ratio = svgAspectRatio(svg)
  const width = Math.max(1, Math.round(ratio >= 1 ? max : max * ratio))
  const height = Math.max(1, Math.round(ratio >= 1 ? max / ratio : max))

  // Firefox falls back to 300x150 for an SVG with no intrinsic size and Safari letterboxes
  // it, so the size is pinned on the root element before the <img> ever sees it.
  svg.setAttribute('width', String(width))
  svg.setAttribute('height', String(height))

  // A data: URL rather than a blob: one — the production CSP (docker/nginx.conf) allows
  // `img-src 'self' data: https:` and no blob:, so an object URL loads under Vite and is
  // silently blocked once deployed. Percent-encoding, not base64: encodeURIComponent gets
  // the UTF-8 right on its own, where btoa() would choke on any non-Latin1 glyph.
  const source = new Image()
  try {
    source.src = `data:${SVG_MIME};charset=utf-8,${encodeURIComponent(
      new XMLSerializer().serializeToString(svg)
    )}`
    await source.decode()
  } catch {
    throw new Error('SVG illisible.')
  }
  return { source, width, height, release: () => {} }
}

function parseSvg(text) {
  const doc = new DOMParser().parseFromString(text, SVG_MIME)
  const svg = doc.documentElement
  if (!svg || svg.tagName.toLowerCase() !== 'svg' || doc.querySelector('parsererror')) {
    throw new Error('SVG illisible.')
  }
  return svg
}

// viewBox wins over width/height: it is the one that survives a logo exported without
// intrinsic dimensions, and it is what the renderer scales by anyway.
function svgAspectRatio(svg) {
  const box = (svg.getAttribute('viewBox') || '').trim().split(/[\s,]+/).map(Number)
  if (box.length === 4 && box.every(Number.isFinite) && box[2] > 0 && box[3] > 0) {
    return box[2] / box[3]
  }
  const width = parseFloat(svg.getAttribute('width'))
  const height = parseFloat(svg.getAttribute('height'))
  if (width > 0 && height > 0) return width / height
  return 1  // no intrinsic size at all: assume square and let object-contain sort it out
}

// Turns the base64 payload the API returns for a fetched URL back into a File, so it can
// go through exactly the same resize path as a hand-picked one. Decoded by hand rather
// than with fetch(dataUrl), which a connect-src CSP would block.
export function dataUrlToFile(dataUrl, name = 'logo') {
  const match = /^data:([^;,]+);base64,([\s\S]*)$/.exec(String(dataUrl || ''))
  if (!match) throw new Error('Réponse illisible.')
  const binary = atob(match[2])
  const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0))
  return new File([bytes], name, { type: match[1] })
}
